import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useDispatch, useSelector } from "react-redux";
import { useOutletContext } from "react-router-dom";
import {
  createTrackingPixel,
  deleteTrackingPixel,
  fetchTrackingPixelsByOrganization,
  trackingPixelsSliceSelector,
  updateTrackingPixel,
} from "../../../../redux/features/trackingPixelsSlice";

const defaultNewPixel = { name: "", type: "meta", pixelId: "" };

const Pixels = () => {
  const { organization } = useOutletContext();
  const { id: organizationId, userIsOwner } = organization;
  const dispatch = useDispatch();
  const trackingPixelsState = useSelector((state) =>
    trackingPixelsSliceSelector(state),
  );
  const { byOrganization } = trackingPixelsState;
  const { pixels = [], loading = false } = byOrganization[organizationId] || {};
  const [editingPixelId, setEditingPixelId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalValues, setModalValues] = useState(defaultNewPixel);
  const [pixelPendingDelete, setPixelPendingDelete] = useState(null);

  useEffect(() => {
    if (organizationId) {
      dispatch(fetchTrackingPixelsByOrganization({ organizationId }));
    }
  }, [dispatch, organizationId]);

  const sortedPixels = useMemo(() => {
    return [...pixels].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [pixels]);

  const canSave = Boolean(modalValues.pixelId?.trim()) && userIsOwner;
  const isEditing = Boolean(editingPixelId);

  const openCreateModal = () => {
    setEditingPixelId(null);
    setModalValues(defaultNewPixel);
    setModalOpen(true);
  };

  const openEditModal = (pixel) => {
    setEditingPixelId(pixel.id);
    setModalValues({
      name: pixel.name || "",
      type: pixel.type || "meta",
      pixelId: pixel.pixelId || "",
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingPixelId(null);
    setModalValues(defaultNewPixel);
  };

  return (
    <Box sx={{ mt: 3, pb: 8 }}>
      <Typography variant="h4">Meta Tracking Pixels</Typography>
      <Typography color="text.secondary" sx={{ mt: 1, mb: 3 }}>
        Create and manage tracking pixels for this organization.
      </Typography>

      <Box sx={{ mb: 3 }}>
        <Button variant="contained" disabled={!userIsOwner} onClick={openCreateModal}>
          Add Pixel
        </Button>
      </Box>

      <Stack spacing={2}>
        {sortedPixels.map((pixel) => {
          return (
            <Paper key={pixel.id} variant="outlined" sx={{ p: 2 }}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={2}
                alignItems={{ md: "center" }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1">
                    {pixel.name?.trim() || "(No name)"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Type: {pixel.type || "meta"} | Pixel ID: {pixel.pixelId}
                  </Typography>
                </Box>
                <Tooltip title="Edit Pixel">
                  <span>
                    <IconButton
                      color="primary"
                      disabled={!userIsOwner}
                      onClick={() => openEditModal(pixel)}
                    >
                      <EditIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Delete Pixel">
                  <span>
                    <IconButton
                      color="error"
                      disabled={!userIsOwner}
                      onClick={() => setPixelPendingDelete(pixel)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            </Paper>
          );
        })}
      </Stack>

      {!loading && sortedPixels.length === 0 && (
        <Typography sx={{ mt: 2 }} color="text.secondary">
          No tracking pixels found for this organization.
        </Typography>
      )}

      <Dialog open={modalOpen} onClose={closeModal} fullWidth maxWidth="sm">
        <DialogTitle>{isEditing ? "Edit Pixel" : "Add Pixel"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              value={modalValues.name}
              onChange={(e) =>
                setModalValues((current) => ({ ...current, name: e.target.value }))
              }
              fullWidth
            />
            <TextField
              select
              label="Type"
              value={modalValues.type}
              onChange={(e) =>
                setModalValues((current) => ({ ...current, type: e.target.value }))
              }
              fullWidth
            >
              <MenuItem value="meta">Meta</MenuItem>
            </TextField>
            <TextField
              label="Pixel ID"
              value={modalValues.pixelId}
              onChange={(e) =>
                setModalValues((current) => ({ ...current, pixelId: e.target.value }))
              }
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModal}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!canSave}
            onClick={async () => {
              const values = {
                ...modalValues,
                name: modalValues.name.trim(),
                pixelId: modalValues.pixelId.trim(),
              };
              if (isEditing) {
                await dispatch(
                  updateTrackingPixel({
                    organizationId,
                    pixelId: editingPixelId,
                    values,
                  }),
                );
              } else {
                await dispatch(
                  createTrackingPixel({
                    organizationId,
                    values,
                  }),
                );
              }
              closeModal();
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(pixelPendingDelete)}
        onClose={() => setPixelPendingDelete(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Delete Pixel?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete{" "}
            <strong>{pixelPendingDelete?.name || pixelPendingDelete?.pixelId}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPixelPendingDelete(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={async () => {
              if (!pixelPendingDelete) return;
              await dispatch(
                deleteTrackingPixel({
                  organizationId,
                  pixelId: pixelPendingDelete.id,
                }),
              );
              setPixelPendingDelete(null);
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Pixels;
